<template>
	<div class="ingame-failed">
		<div class="ingame-failed__header">
			<div class="ingame-failed__header__title">
				{{ $t('checkout.purchase-failed') }}
			</div>
			<div class="ingame-failed__header__message">
				<div v-if="isMileageError">
					{{ mileageErrorMsg }}
				</div>
				<div v-else>
					{{
						$te(`error.form.${errorCode}`)
							? $t(`error.form.${errorCode}`)
							: $t('checkout.unable-to-complete-purchase')
					}}
				</div>
				<div v-if="errorCode">({{ $t('checkout.error-code', { errorCode }) }})</div>
			</div>
		</div>

		<div v-if="orderNumber" class="ingame-failed__details">
			<div class="order-details">
				<div class="order-details__row">
					<div class="order-details__row__title">{{ $t('checkout.order-number') }}</div>
					<div class="order-details__row__value">{{ orderNumber }}</div>
				</div>
			</div>
		</div>

		<i18n v-if="isMileageError" class="ingame-failed__contacts" :path="mileageErrorContactPath" tag="div">
			<template v-slot:supportLink>
				<a :href="MILEAGE_INFO.SUPPORT_LINK" target="_blank" @click="supportTracking">
					{{ $t('mileage.error.support-link') }}
				</a>
			</template>
		</i18n>
		<i18n v-else class="ingame-failed__contacts" path="checkout.for-information-billing" tag="div">
			<template v-slot:nexonSupportCenter>
				<a :href="supportLink" target="_blank" @click="supportTracking">
					{{ $t('checkout.nexon-support-center') }}
				</a>
			</template>
		</i18n>

		<div class="ingame-failed__action">
			<FormButton class="btn-primary" :label="$t('checkout.close-window')" :button-click="close"></FormButton>
		</div>
	</div>
</template>

<script>
import { FormButton } from '@arena/toolkit';
import resultMixin from './result-mixin';

const MILEAGE_INFO = {
	SUPPORT_LINK: 'https://playersupport.nexon.com/hc/en-us?_ga=2.65007915.583200399.1741932959-15862622.1741932959',
	ERROR_CODE: {
		NOT_ENOUGH: '003-400-0030',
		ETC: ['003-500-0031', '003-400-0032', '003-400-0030'],
	},
};

export default {
	name: 'InGamePurchaseFailed',
	components: {
		FormButton,
	},
	mixins: [resultMixin],
	props: {
		errorCode: {
			type: [String, Number],
			default: null,
		},
		mileageName: {
			type: String,
			default: null,
		},
	},
	data() {
		return {
			MILEAGE_INFO: MILEAGE_INFO,
		};
	},
	computed: {
		sufficientQuery() {
			return this.$route.query.sufficient;
		},
		igpSufficient() {
			if (this.sufficientQuery === 'unknown') {
				return 'unknown';
			} else if (this.sufficientQuery === true || this.sufficientQuery === 'true') {
				// if the page is refreshed then it's string not boolean so we should check if it's true or 'true'
				return true;
			}
			return false;
		},
		paymentMethod() {
			return (
				this.inGameTransactionResult &&
				this.inGameTransactionResult.payment &&
				this.inGameTransactionResult.payment.paymentMethod
			);
		},
		isMileageError() {
			return (
				this.errorCode === this.MILEAGE_INFO.ERROR_CODE.NOT_ENOUGH ||
				this.MILEAGE_INFO.ERROR_CODE.ETC.includes(this.errorCode)
			);
		},
		mileageErrorMsg() {
			if (this.errorCode === this.MILEAGE_INFO.ERROR_CODE.NOT_ENOUGH) {
				return this.$t('mileage.error.not-enough', { currency: this.mileageName });
			}

			return this.$t('mileage.error.etc');
		},
		mileageErrorContactPath() {
			if (this.errorCode === this.MILEAGE_INFO.ERROR_CODE.NOT_ENOUGH) {
				return 'mileage.error.not-enough-desc';
			}

			return 'mileage.error.etc-desc';
		},
	},
	mounted() {
		this.$a2s.view('error_imp', {
			name: 'error_code',
			value: this.errorCode,
			objectTag: 'purchase_error',
		});

		let igpError;
		if (this.errorCode) {
			igpError = `${this.errorCode} ${
				this.$te(`error.form.${this.errorCode}`)
					? this.$t(`error.form.${this.errorCode}`, 'en')
					: this.$t('checkout.unable-to-complete-purchase', 'en')
			}`;
		} else {
			igpError = this.$t('checkout.unable-to-complete-purchase', 'en');
		}
		dataLayer.push({
			event: 'In Game Purchase Failure',
			...(this.igpSufficient !== 'unknown' ? { IGP_sufficient: this.igpSufficient } : {}),
			...(this.paymentMethod ? { IGP_paymentGateway: this.paymentMethod } : {}),
			IGP_error: igpError,
		});
	},
};
</script>

<style lang="scss" scoped>
.ingame-failed {
	max-width: 640px;
	margin-left: auto;
	margin-right: auto;
	&__header {
		color: #fff;
		text-align: center;
		line-height: 1;
		padding-bottom: 16px;
		border-bottom: 2px solid rgba(#fff, 0.5);

		&:before {
			content: '';
			display: block;
			width: 100%;
			height: 100px;
			background-image: url('~@/images/purchase-failed.png');
			background-repeat: no-repeat;
			background-size: contain;
			background-position: center center;
			margin-top: 40px;

			@media (max-width: 700px) {
				content: none;
			}
		}
		&__title {
			font-size: 24px;
			padding-top: 13px;
		}
		&__message {
			padding-top: 10px;
			line-height: 1.3;
			color: #ff1744;
		}
	}
	&__details {
		border-bottom: 1px solid rgba(#fff, 0.2);
		.order-details {
			padding-bottom: 14px;
			&__row {
				display: flex;
				flex-wrap: wrap;
				justify-content: space-between;
				padding-top: 14px;
				line-height: 1.4;

				&__title,
				&__value {
					display: block;
					max-width: 100%;
				}
				&__title {
					margin-right: auto;
					padding-right: 15px;
				}
				&__value {
					color: #fff;
				}

				@media (max-width: 600px) {
					justify-content: start;
					&__title,
					&__value {
						flex: 0 0 100%;
						max-width: 100%;
					}
					&__value {
						word-break: break-word;
						word-wrap: break-word;
					}
				}
			}
		}
	}
	&__contacts {
		text-align: center;
		padding-top: 34px;
		line-height: 1.3;
		white-space: pre-wrap;
		a {
			text-decoration: underline;
			text-underline-position: under;
			text-transform: capitalize;
			color: inherit;
			&:hover {
				color: #fff;
			}
		}
	}
	&__action {
		button {
			min-width: 320px;
			width: auto;
			margin-top: 35px;
			margin-left: auto;
			margin-right: auto;

			@media (max-width: 700px) {
				min-width: 100%;
				width: 100%;
			}
		}
	}
}
</style>
